import { entities } from "@oxygen/concepts";
import type { LucideIcon } from "lucide-react";
import {
	ArrowRightLeft,
	ArrowUpFromLine,
	BellRing,
	Box,
	CreditCard,
	DollarSign,
	FileText,
	HelpCircle,
	KeyRound,
	Landmark,
	Mail,
	Receipt,
	RotateCcw,
	Send,
	Server,
	Settings,
	ShieldAlert,
	Store,
	Users,
	Wallet,
} from "lucide-react";

export interface EntityConfig {
	icon: LucideIcon;
	color: string;
	label: string;
}

const fromShared = (id: keyof typeof entities): EntityConfig => {
	const e = entities[id];
	return { icon: e.icon, color: e.color, label: e.label.plural };
};

export const ENTITY_CONFIG: Record<string, EntityConfig> = {
	// Shared concepts (visual source of truth in @oxygen/concepts)
	dashboard: fromShared("dashboard"),
	order: fromShared("orders"),
	payment: fromShared("payments"),
	settings: fromShared("settings"),

	// Admin-only entities
	revenue: { icon: DollarSign, color: "#22c55e", label: "Revenue" },
	treasury: { icon: Landmark, color: "#14b8a6", label: "Treasury" },
	user: { icon: Users, color: "#3b82f6", label: "Users" },
	merchant: { icon: Store, color: "#8b5cf6", label: "Merchants" },
	wallet: { icon: Wallet, color: "#f59e0b", label: "Wallets" },
	transaction: {
		icon: ArrowRightLeft,
		color: "#10b981",
		label: "Transactions",
	},
	invitation: { icon: Mail, color: "#06b6d4", label: "Invitations" },
	alert: { icon: BellRing, color: "#f97316", label: "Alerts" },
	aml: { icon: ShieldAlert, color: "#8b5cf6", label: "AML Review" },
	fund_return: { icon: RotateCcw, color: "#ec4899", label: "Fund Returns" },
	orphan_transaction: {
		icon: HelpCircle,
		color: "#a855f7",
		label: "Unattributed Deposits",
	},
	pool_withdrawal: {
		icon: ArrowUpFromLine,
		color: "#0ea5e9",
		label: "Pool Withdrawals",
	},
	audit_log: { icon: FileText, color: "#64748b", label: "Audit Logs" },
	system_settings: {
		icon: Settings,
		color: "#6b7280",
		label: "System Settings",
	},
	currency_settings: {
		icon: DollarSign,
		color: "#f59e0b",
		label: "Currency Settings",
	},
	system: { icon: Server, color: "#6b7280", label: "System" },
	api_token: { icon: KeyRound, color: "#ef4444", label: "API Token" },
};

export const ALERT_CATEGORY_CONFIG: Record<string, EntityConfig> = {
	blockchain: { icon: ArrowRightLeft, color: "#10b981", label: "Blockchain" },
	transaction: { icon: ArrowRightLeft, color: "#10b981", label: "Transaction" },
	payment: { icon: CreditCard, color: "#14b8a6", label: "Payment" },
	order: { icon: Receipt, color: "#6366f1", label: "Order" },
	wallet: { icon: Wallet, color: "#f59e0b", label: "Wallet" },
	balance: { icon: Wallet, color: "#f59e0b", label: "Balance" },
	withdrawal: {
		icon: ArrowUpFromLine,
		color: "#0ea5e9",
		label: "Withdrawal",
	},
	security: { icon: ShieldAlert, color: "#ef4444", label: "Security" },
	aml: { icon: ShieldAlert, color: "#8b5cf6", label: "AML" },
	system: { icon: Server, color: "#6b7280", label: "System" },
	webhook_delivery: {
		icon: Send,
		color: "#f97316",
		label: "Webhook Delivery",
	},
	delivery: { icon: Send, color: "#f97316", label: "Delivery" },
	provider: { icon: Server, color: "#6b7280", label: "Provider" },
	merchant: { icon: Store, color: "#8b5cf6", label: "Merchant" },
	user: { icon: Users, color: "#3b82f6", label: "User" },
};

export const AUDIT_CATEGORY_CONFIG: Record<string, EntityConfig> = {
	user_management: { icon: Users, color: "#3b82f6", label: "User Management" },
	merchant_management: {
		icon: Store,
		color: "#8b5cf6",
		label: "Merchant Management",
	},
	invitation_management: {
		icon: Mail,
		color: "#06b6d4",
		label: "Invitation Management",
	},
	wallet_operation: {
		icon: Wallet,
		color: "#f59e0b",
		label: "Wallet Operation",
	},
	payment_management: {
		icon: CreditCard,
		color: "#14b8a6",
		label: "Payment Management",
	},
	authentication: {
		icon: KeyRound,
		color: "#22c55e",
		label: "Authentication",
	},
	configuration: { icon: Settings, color: "#6b7280", label: "Configuration" },
	system_operation: {
		icon: Server,
		color: "#ef4444",
		label: "System Operation",
	},
	compliance_aml: {
		icon: ShieldAlert,
		color: "#8b5cf6",
		label: "AML Compliance",
	},
};

export const AUDIT_RESOURCE_CONFIG: Record<string, EntityConfig> = {
	user: ENTITY_CONFIG.user,
	merchant: ENTITY_CONFIG.merchant,
	invitation: ENTITY_CONFIG.invitation,
	wallet: ENTITY_CONFIG.wallet,
	transaction: ENTITY_CONFIG.transaction,
	order: ENTITY_CONFIG.order,
	payment: ENTITY_CONFIG.payment,
	api_token: ENTITY_CONFIG.api_token,
	system: ENTITY_CONFIG.system,
};

const DEFAULT_CONFIG: EntityConfig = {
	icon: Box,
	color: "#6b7280",
	label: "Unknown",
};

export const getEntityConfig = (entity: string): EntityConfig =>
	ENTITY_CONFIG[entity.toLowerCase()] || DEFAULT_CONFIG;

export const getCategoryConfig = (category: string): EntityConfig =>
	AUDIT_CATEGORY_CONFIG[category] || DEFAULT_CONFIG;

export const getResourceConfig = (resourceType: string): EntityConfig =>
	AUDIT_RESOURCE_CONFIG[resourceType.toLowerCase()] || DEFAULT_CONFIG;

export const getAlertCategoryConfig = (category: string): EntityConfig => {
	const normalized = category.toLowerCase().replace(/-/g, "_");
	return (
		ALERT_CATEGORY_CONFIG[category] ||
		ALERT_CATEGORY_CONFIG[normalized] ||
		DEFAULT_CONFIG
	);
};
